/**
 * nebulaArtifactHunks.js
 *
 * Groups the raw line-by-line output of `diffArtifacts()` into discrete
 * "hunks" — contiguous regions of change separated by unchanged context.
 * Each hunk is what a user accepts or rejects as a single unit (Phase B).
 *
 * Hunk shape:
 *   {
 *     id: string                  // stable across re-renders, derived from index + text fingerprint
 *     beforeText: string          // text in pre-edit file at this region (joined w/ \n)
 *     afterText: string           // text in post-edit file at this region (joined w/ \n)
 *     beforeLineCount: number     // lines beforeText spans in the pre-edit file
 *     afterLineCount: number      // lines afterText spans in the post-edit file
 *     // Display helpers (line numbers in the post-edit file):
 *     startLineNew: number        // 1-based first line of the hunk's added/replacement region
 *     endLineNew: number          // 1-based last line (inclusive); === startLineNew - 1 for pure-deletion hunks
 *     addedLineNos: number[]      // lines (in post-edit) carrying "added" highlight
 *     removedTexts: string[]      // verbatim removed lines (rendered as ghost widgets at startLineNew)
 *   }
 */

/**
 * @param {Array<{type:'added'|'removed'|'unchanged'|'collapsed', value:string, count?:number}>} diffLines
 * @returns {Array<object>} hunks
 */
export function groupIntoHunks(diffLines) {
  if (!diffLines || diffLines.length === 0) return [];

  const hunks = [];
  let newLineNo = 1;        // tracks line position in the post-edit file
  let curAdded = [];        // lines added in the current hunk being assembled
  let curAddedLineNos = []; // their 1-based line numbers in post-edit file
  let curRemoved = [];      // lines removed in the current hunk
  let curStartLineNew = null; // first added line of current hunk (or current newLineNo for pure-del)

  const flush = () => {
    if (curAdded.length === 0 && curRemoved.length === 0) return;
    const startLine = curStartLineNew ?? newLineNo;
    const endLine = curAdded.length > 0 ? curAddedLineNos[curAddedLineNos.length - 1] : startLine - 1;
    const beforeText = curRemoved.join("\n");
    const afterText = curAdded.join("\n");
    // Derive a stable id from index + a short fingerprint of the change. Index
    // alone would collide if the user rejects a hunk and the same shape recurs
    // in re-derived hunks, so include a hash-like prefix of the content.
    const fingerprint = (beforeText + "|" + afterText).slice(0, 80);
    hunks.push({
      id: `h${hunks.length}:${startLine}:${fingerprint.length}`,
      beforeText,
      afterText,
      beforeLineCount: curRemoved.length,
      afterLineCount: curAdded.length,
      startLineNew: startLine,
      endLineNew: endLine,
      addedLineNos: curAddedLineNos.slice(),
      removedTexts: curRemoved.slice(),
    });
    curAdded = [];
    curAddedLineNos = [];
    curRemoved = [];
    curStartLineNew = null;
  };

  for (const entry of diffLines) {
    if (entry.type === "collapsed") {
      flush();
      newLineNo += entry.count ?? 0;
    } else if (entry.type === "unchanged") {
      flush();
      newLineNo += 1;
    } else if (entry.type === "removed") {
      if (curStartLineNew == null) curStartLineNew = newLineNo;
      curRemoved.push(entry.value);
      // newLineNo does NOT advance — removed lines aren't in the post-edit file
    } else if (entry.type === "added") {
      if (curStartLineNew == null) curStartLineNew = newLineNo;
      curAdded.push(entry.value);
      curAddedLineNos.push(newLineNo);
      newLineNo += 1;
    }
  }
  flush();
  return hunks;
}

/**
 * Build the displayed code string by walking the original pre-edit code,
 * applying or reverting each hunk based on the user's decisions.
 *
 * @param {string} preEditCode    snapshot taken before the AI edit ran
 * @param {string} postEditCode   model-merged result (all hunks applied)
 * @param {Array<object>} hunks   from groupIntoHunks(), order MUST match the diff order
 * @param {Map<string,'rejected'>} decisions  hunk.id → "rejected"; absent = applied
 * @returns {string} the code that should be shown in the editor
 *
 * Implementation: we don't try to splice in line space (line numbers shift as
 * hunks change). Instead we re-derive the file by walking the diff: for each
 * hunk, emit beforeText if rejected, afterText otherwise. Unchanged regions
 * pass through unchanged.
 *
 * To do this without re-running the LCS, we rely on the invariant that the
 * concatenation of (unchanged segments + each hunk's afterText) === postEditCode.
 * So we extract the unchanged scaffolding once by removing each hunk's
 * afterText from postEditCode, then re-assemble with the chosen variant.
 */
export function buildDisplayedCode(preEditCode, postEditCode, hunks, decisions) {
  if (!hunks || hunks.length === 0) return postEditCode;
  if (!decisions || decisions.size === 0) return postEditCode;

  // Work in line space — much easier than char-offset splicing.
  const postLines = postEditCode.split("\n");
  const outLines = [];
  let cursor = 1; // 1-based line cursor in postLines

  for (const h of hunks) {
    // Emit unchanged lines from cursor up to (but not including) startLineNew
    if (cursor < h.startLineNew) {
      outLines.push(...postLines.slice(cursor - 1, h.startLineNew - 1));
      cursor = h.startLineNew;
    }
    const rejected = decisions.get(h.id) === "rejected";
    if (rejected) {
      // Replace the hunk's after-region with its before-region
      if (h.beforeLineCount > 0) {
        outLines.push(...h.beforeText.split("\n"));
      }
      cursor += h.afterLineCount;
    } else {
      // Keep the hunk as-is (afterText)
      outLines.push(...postLines.slice(cursor - 1, cursor - 1 + h.afterLineCount));
      cursor += h.afterLineCount;
    }
  }
  // Trailing unchanged tail
  if (cursor <= postLines.length) {
    outLines.push(...postLines.slice(cursor - 1));
  }
  return outLines.join("\n");
}

/**
 * Compute display metadata (added line numbers + ghosts) for the CURRENT
 * displayed code given the user's per-hunk decisions. Used to refresh the
 * CodeMirror diff extension after an accept/reject.
 *
 * Returns the same shape as getDiffLineMap() but only for hunks that are
 * still pending (not rejected). Accepted hunks render no decoration. Rejected
 * hunks render as the original code with no decoration (they've reverted).
 *
 * Also returns each pending hunk's startLine in the displayed code so the
 * caller can render Accept/Reject buttons and implement next/prev navigation.
 */
export function getHunkDisplayMap(displayedCode, hunks, decisions) {
  const decisionsMap = decisions || new Map();
  // Walk hunks; for each, locate its position in displayedCode by re-tracking
  // line position across applied/rejected segments.
  const addedLineNos = [];
  const ghostsByLineNo = new Map();
  const hunkAnchors = []; // [{id, displayLine, status}]

  // Re-derive segments to get the correct line walk (mirrors buildDisplayedCode).
  // To keep the implementation simple we recompute by line counts:
  //   - "fixed" segments contribute their original post-edit unchanged span
  //   - "hunk" segments contribute afterLineCount lines if applied, beforeLineCount if rejected
  let cursorLine = 1; // line in displayed code

  // We need the post-edit line spans of each fixed gap. Reconstruct from hunks order.
  let postCursor = 1; // line cursor in post-edit code
  for (const h of hunks) {
    const gapLines = h.startLineNew - postCursor;
    cursorLine += gapLines;
    postCursor += gapLines;

    const status = decisionsMap.get(h.id);
    if (status === "rejected") {
      // The hunk shows beforeText (no decorations); advance both cursors
      cursorLine += h.beforeLineCount;
      postCursor += h.afterLineCount;
    } else if (status === "accepted") {
      // User has individually accepted this hunk — drop its anchor so the
      // controls disappear, but keep the lines themselves un-highlighted so
      // the user can focus on remaining pending decisions.
      cursorLine += h.afterLineCount;
      postCursor += h.afterLineCount;
    } else {
      // Pending — render decorations + Accept/Reject controls.
      const offset = cursorLine - h.startLineNew;
      for (const ln of h.addedLineNos) addedLineNos.push(ln + offset);
      const anchorLine = h.afterLineCount > 0 ? cursorLine : Math.max(1, cursorLine);
      if (h.removedTexts.length > 0) {
        const existing = ghostsByLineNo.get(anchorLine) || [];
        ghostsByLineNo.set(anchorLine, [...existing, ...h.removedTexts]);
      }
      hunkAnchors.push({
        id: h.id,
        displayLine: anchorLine,
        status: "pending",
      });
      cursorLine += h.afterLineCount;
      postCursor += h.afterLineCount;
    }
  }

  return { addedLineNos, removedLineNos: [], ghostsByLineNo, hunkAnchors };
}
